import { useEffect, useMemo, useState } from 'react';
// @mui
import {
  Card,
  Stack,
  Table,
  TextField,
  Container,
  TableRow,
  TableBody,
  Chip,
  TableCell,
  TableHead,
  Typography,
  InputAdornment,
  TableContainer,
  LinearProgress,
  TablePagination,
} from '@mui/material';
// hooks
import useSettings from 'src/hooks/useSettings';
// components
import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import { TableSkeleton } from 'src/components/table';
// routes
import { PATH_DASHBOARD } from 'src/routes/paths';
// utils
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { fDateTimeSuffix } from 'src/utils/formatTime';

// ----------------------------------------------------------------------
// Contact-us messages as a standard admin table (Date · Name · Email · Message)
// with a single Students-style search box that filters across every column.

type ContactMessage = {
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  category?: 'technical' | 'administrative';
  createdAt?: string;
};

function safeDate(value?: string): string {
  if (!value) return '—';
  try {
    return fDateTimeSuffix(new Date(value));
  } catch {
    return value;
  }
}

export const Messages = () => {
  const { themeStretch } = useSettings();
  const [rows, setRows] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(APIS.CONTACT.LIST);
        const data = res.data?.data ?? res.data;
        setRows(Array.isArray(data) ? data : data?.items ?? []);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Search box across all columns + a "created at" date-range filter (From/To).
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      const created = (r.createdAt || '').slice(0, 10); // YYYY-MM-DD
      if (fromDate && (!created || created < fromDate)) return false;
      if (toDate && (!created || created > toDate)) return false;
      if (s) {
        const hay = [safeDate(r.createdAt), r.name, r.email, r.phone, r.message, r.category]
          .map((x) => (x || '').toString().toLowerCase())
          .join(' ');
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [rows, search, fromDate, toDate]);

  const paged = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Page title="Messages">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Messages"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Messages' },
          ]}
        />

        <Card>
          <Stack
            spacing={2}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            sx={{ py: 2.5, px: 3, flexWrap: 'wrap' }}
          >
            <TextField
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search messages..."
              sx={{ flexGrow: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon={'eva:search-fill'}
                      sx={{ color: 'text.disabled', width: 20, height: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              type="date"
              size="small"
              label="From"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toDate || undefined }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fromDate || undefined }}
              sx={{ minWidth: 150 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {filtered.length} of {rows.length}
            </Typography>
          </Stack>

          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
              />
            )}
            <Table size="medium" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading &&
                  [...Array(6)].map((_, i) => <TableSkeleton key={`msg-skeleton-${i}`} />)}
                {!loading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      No messages found.
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  paged.map((r, i) => (
                    <TableRow key={r._id || i} hover>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {safeDate(r.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>
                          {r.name || '—'}
                        </Typography>
                        {r.phone && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {r.phone}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {r.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.category === 'technical' ? 'Technical' : 'Administrative'}
                          color={r.category === 'technical' ? 'warning' : 'default'}
                          variant={r.category === 'technical' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2">{r.message || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Card>
      </Container>
    </Page>
  );
};
